import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { HugeiconsIcon } from '@hugeicons/react';
import { 
  ArrowLeft01Icon, 
  UserGroupIcon,
  Shield02Icon,
  AlertCircleIcon
} from '@hugeicons/core-free-icons';
import { api } from '../../utils/api';
import { FadeLoader } from 'react-spinners';
import Button from '../../components/Button';

const AdminOrderDetail = () => {
  const { orderId } = useParams(); // Using orderId as route param but it matches Tx reference/uid
  const navigate = useNavigate();
  const [tx, setTx] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTx = async () => {
      try {
        const res = await api.get(`/admin/transactions/${orderId}`);
        setTx(res.data);
      } catch (err) {
        console.error("Failed to fetch transaction:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchTx();
  }, [orderId]);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><FadeLoader color="#4ade80" /></div>;
  if (!tx) return <div className="p-20 text-center font-black uppercase">Transaction Not Found</div>;

  return (
    <div className="max-w-4xl mx-auto space-y-8 pb-20 px-4 sm:px-8 pt-6">
      <button onClick={() => navigate('/admin')} className="flex items-center gap-2 text-muted-gray hover:text-charcoal dark:hover:text-white transition-colors font-black text-sm uppercase tracking-widest">
        <HugeiconsIcon icon={ArrowLeft01Icon} size={18} /> Back to Admin
      </button>

      <div className="bg-white dark:bg-[#111] p-8 rounded-[3rem] border border-gray-100 dark:border-white/10 shadow-sm flex flex-col md:flex-row gap-8 items-center justify-between">
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-black uppercase text-muted-gray tracking-widest">Transaction Reference</p>
          <h1 className="text-xl sm:text-3xl font-black font-mono break-all mt-1">{tx.reference}</h1>
        </div>
        <div className="text-center md:text-right shrink-0">
          <span className={`px-6 py-2 rounded-full text-xs font-black uppercase tracking-widest ${tx.status === 'completed' ? 'bg-green-500 text-white' : 'bg-yellow-500 text-white'}`}>
            {tx.status}
          </span>
          <p className="text-xs text-muted-gray mt-2 font-medium">{new Date(tx.created_at).toLocaleString()}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
        <div className="bg-white dark:bg-[#111] p-6 rounded-[2rem] border border-gray-100 dark:border-white/10 shadow-sm">
          <p className="text-[10px] font-black uppercase text-muted-gray mb-2">Fiat Amount</p>
          <p className="text-2xl font-black text-soft-green">₦{parseFloat(tx.amount_sent).toLocaleString()}</p>
        </div>
        <div className="bg-white dark:bg-[#111] p-6 rounded-[2rem] border border-gray-100 dark:border-white/10 shadow-sm">
          <p className="text-[10px] font-black uppercase text-muted-gray mb-2">USD Equivalent</p>
          <p className="text-2xl font-black text-charcoal dark:text-white">${parseFloat(tx.amount_usd || 0).toFixed(2)}</p>
        </div>
        <div className="bg-white dark:bg-[#111] p-6 rounded-[2rem] border border-gray-100 dark:border-white/10 shadow-sm">
          <p className="text-[10px] font-black uppercase text-muted-gray mb-2">Exchange Rate</p>
          <p className="text-2xl font-black text-charcoal dark:text-white">₦{parseFloat(tx.order?.current_rate || 0).toLocaleString()}/$</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Parties */}
        <div className="bg-white dark:bg-[#111] p-8 rounded-[2.5rem] border border-gray-100 dark:border-white/10 shadow-sm space-y-6">
          <h3 className="text-xl font-black flex items-center gap-2">
            <HugeiconsIcon icon={UserGroupIcon} size={24} /> 
            Parties Involved
          </h3>
          <div className="space-y-4">
            <div className="p-4 bg-gray-50 dark:bg-white/5 rounded-2xl flex justify-between">
              <div><p className="text-[10px] font-black uppercase text-muted-gray">Client</p><p className="font-bold">{tx.client?.name || 'N/A'}</p><p className="text-xs text-muted-gray">{tx.client?.email}</p></div>
              <Button onClick={() => navigate(`/admin/users/${tx.client?.uid}`)} className="h-fit px-3 py-1.5 rounded-lg text-[10px] font-black">Profile</Button>
            </div>
            <div className="p-4 bg-gray-50 dark:bg-white/5 rounded-2xl flex justify-between">
              <div><p className="text-[10px] font-black uppercase text-muted-gray">Merchant/Agent</p><p className="font-bold">{tx.order?.agent?.name || tx.order?.agent?.business_name || 'N/A'}</p><p className="text-xs text-muted-gray">{tx.order?.agent?.email}</p></div>
              <Button onClick={() => navigate(`/admin/users/${tx.order?.agent?.uid}`)} className="h-fit px-3 py-1.5 rounded-lg text-[10px] font-black">Profile</Button>
            </div>
          </div>
        </div>

        {/* Technical Metadata */}
        <div className="bg-white dark:bg-[#111] p-8 rounded-[2.5rem] border border-gray-100 dark:border-white/10 shadow-sm space-y-6">
          <h3 className="text-xl font-black flex items-center gap-2">
            <HugeiconsIcon icon={Shield02Icon} size={24} /> 
            Audit Trail
          </h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center py-2 border-b border-gray-100 dark:border-white/10">
              <span className="text-[10px] font-black uppercase text-muted-gray">Internal ID</span>
              <span className="font-mono text-sm">{tx.uid}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-gray-100 dark:border-white/10">
              <span className="text-[10px] font-black uppercase text-muted-gray">Order Token</span>
              <span className="font-mono text-sm">{tx.order?.link_token}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-gray-100 dark:border-white/10">
              <span className="text-[10px] font-black uppercase text-muted-gray">Verified By</span>
              <span className="text-sm font-bold">{tx.verified_by?.name || 'System Auto-Verify'}</span>
            </div>
          </div>
        </div>
      </div>

      {tx.agent_note && (
        <div className="bg-blue-50 dark:bg-blue-900/20 p-8 rounded-[2.5rem] border border-blue-100 dark:border-blue-800/30">
          <h3 className="text-lg font-black text-blue-800 dark:text-blue-200 flex items-center gap-2 mb-2">
            <HugeiconsIcon icon={AlertCircleIcon} size={20} /> 
            Agent / System Note
          </h3>
          <p className="text-blue-700 dark:text-blue-300 font-medium">{tx.agent_note}</p>
        </div>
      )}
    </div>
  );
};

export default AdminOrderDetail;